// The COMMON dashboard's cross-domain read: coverage and issues fused per mandal. Pure functions —
// the Field-picture quadrant scatter, the district scorecard (league table) and the scoped pulse
// all derive from these so the three modules can never disagree with each other.

import { healthVerdict, issueControl, mandalHealth, type Verdict } from './health'

import type { MandalAggregate } from '../api/types'

/** The coverage bar every gap-based read uses (same 80% bar as the Attention Score's gap terms). */
export const COVERAGE_BAR = 80

export type QuadrantKey = 'danger' | 'firefighting' | 'blindspot' | 'healthy'

export const QUADRANT_META: Record<QuadrantKey, { label: string; color: string; hint: string }> = {
  danger: { label: 'Danger zone', color: '#dc2626', hint: 'low coverage, high issue load' },
  firefighting: { label: 'Firefighting', color: '#ea580c', hint: 'well surveyed, high issue load' },
  blindspot: { label: 'Blind spot?', color: '#94a3b8', hint: 'low coverage — few issues FOUND, not few issues' },
  healthy: { label: 'Healthy', color: '#10b981', hint: 'well surveyed, low issue load' },
}

/** Open issues per 10 sites — the size-normalised load a mandal carries (0 for empty mandals). */
export function issueDensity(m: MandalAggregate): number {
  if (m.siteCount <= 0) {
    return 0
  }
  return Math.round(((m.openIssues * 10) / m.siteCount) * 10) / 10
}

/** Median of a list (0 for empty). Used as the load cut so quadrants adapt to the district's scale. */
export function median(values: number[]): number {
  if (values.length === 0) {
    return 0
  }
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2
}

export interface FieldPoint {
  name: string
  /** Null coverage plots at 0 (an unsurveyed mandal IS a coverage-0 mandal for this read). */
  coverage: number
  density: number
  siteCount: number
  openIssues: number
  openCritical: number
  quadrant: QuadrantKey
}

export interface FieldPicture {
  points: FieldPoint[]
  /** The issue-load cut between "low" and "high" (median density, floored at 1 per 10 sites). */
  densityCut: number
  /** Points per quadrant (the legend counts). */
  counts: Record<QuadrantKey, number>
}

const quadrantOf = (coverage: number, density: number, cut: number): QuadrantKey => {
  const lowCoverage = coverage < COVERAGE_BAR
  const highLoad = density >= cut
  if (lowCoverage && highLoad) {
    return 'danger'
  }
  if (!lowCoverage && highLoad) {
    return 'firefighting'
  }
  if (lowCoverage) {
    return 'blindspot'
  }
  return 'healthy'
}

/** The quadrant scatter's model: every mandal placed by coverage (x) vs issue load (y). */
export function fieldPicture(mandals: MandalAggregate[]): FieldPicture {
  const withSites = mandals.filter((m) => m.siteCount > 0)
  const densities = withSites.map(issueDensity)
  const densityCut = Math.max(1, median(densities.filter((d) => d > 0)))
  const points = withSites.map((m) => {
    const coverage = m.coverage ?? 0
    const density = issueDensity(m)
    return {
      name: m.name,
      coverage,
      density,
      siteCount: m.siteCount,
      openIssues: m.openIssues,
      openCritical: m.openCritical,
      quadrant: quadrantOf(coverage, density, densityCut),
    }
  })
  const counts: Record<QuadrantKey, number> = { danger: 0, firefighting: 0, blindspot: 0, healthy: 0 }
  for (const p of points) {
    counts[p.quadrant] += 1
  }
  return { points, densityCut, counts }
}

export interface ScorecardRow {
  name: string
  health: number
  verdict: Verdict
  coverage: number | null
  openIssues: number
  openCritical: number
  overdueSites: number
  neverSurveyedSites: number
}

/** The league table: every scoped mandal, worst health first (ties → more critical first). */
export function scorecardRows(mandals: MandalAggregate[]): ScorecardRow[] {
  return mandals
    .filter((m) => m.siteCount > 0)
    .map((m) => {
      const health = mandalHealth(m)
      return {
        name: m.name,
        health,
        verdict: healthVerdict(health),
        coverage: m.coverage,
        openIssues: m.openIssues,
        openCritical: m.openCritical,
        overdueSites: m.overdueSites,
        neverSurveyedSites: m.neverSurveyedSites,
      }
    })
    .sort((a, b) => a.health - b.health || b.openCritical - a.openCritical || b.openIssues - a.openIssues)
}

export interface ScopedPulse {
  health: number
  coverage: number | null
  officers: number | null
  control: number
}

/**
 * The pulse bars for a MANDAL SCOPE — site-weighted coverage/officer means + the critical share of
 * the scoped open load, run through the same `healthFrom` composition as the district read. (The
 * unscoped pulse uses `overview.summary` directly — the server's own rollup.)
 */
export function scopedPulse(mandals: MandalAggregate[]): ScopedPulse {
  const withSites = mandals.filter((m) => m.siteCount > 0)
  const weight = withSites.reduce((a, m) => a + m.siteCount, 0)
  const wavg = (pick: (m: MandalAggregate) => number | null): number | null => {
    const rows = withSites.filter((m) => pick(m) !== null)
    const w = rows.reduce((a, m) => a + m.siteCount, 0)
    if (w === 0) {
      return null
    }
    return Math.round(rows.reduce((a, m) => a + (pick(m) ?? 0) * m.siteCount, 0) / w)
  }
  const coverage = weight === 0 ? null : wavg((m) => m.coverage)
  const officers = weight === 0 ? null : wavg((m) => m.officerAvgCompletion)
  const openTotal = withSites.reduce((a, m) => a + m.openIssues, 0)
  const criticalTotal = withSites.reduce((a, m) => a + m.openCritical, 0)
  const criticalShare = openTotal > 0 ? Math.round((criticalTotal / openTotal) * 100) : null
  const control = issueControl(criticalShare)
  const health = Math.round(((coverage ?? 0) + (officers ?? 0) + control) / 3)
  return { health, coverage, officers, control }
}
